/**
 * COERCE FIRST — the API sends Decimal columns as STRINGS.
 *
 * At runtime these helpers receive strings like "103.48000000" straight out of a
 * Prisma Decimal. `String.prototype.toLocaleString()` accepts and then IGNORES
 * the options object, returning the string untouched — so
 * `formatPrice("103.48000000", 2)` silently produced "103.48000000". Every
 * decimal-precision fix upstream of this was being discarded here, which is why
 * the Dashboard kept printing eight decimals no matter what precision was
 * resolved for it.
 *
 * The coercion was added first; the TYPES still said `value: number`, which is
 * what let a string reach them undetected in the first place. They now say what
 * actually arrives. Every existing caller passing a number still type-checks.
 */
export type Numeric = number | string | null | undefined;
const toNum = (value: unknown): number | null => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

export const formatCurrency = (value: Numeric, currency = 'INR', decimals = 2): string => {
  const n = toNum(value);
  if (n == null) return String(value ?? '');
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(n);
};

export const formatPrice = (value: Numeric, decimals = 2): string => {
  const n = toNum(value);
  if (n == null) return String(value ?? '');
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/* Was never coerced: it called `.toFixed()` straight on the argument. On a
   Decimal string that is a TypeError, not a wrong string — `pnlPercent` only
   escaped because its controller happens to parseFloat it first. */
export const formatPercentage = (value: Numeric): string => {
  const n = toNum(value);
  if (n == null) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
};

export const formatNumber = (value: Numeric, decimals = 2): string => {
  const n = toNum(value);
  if (n == null) return String(value ?? '');
  return n.toFixed(decimals);
};

/**
 * Signed currency for a P&L figure: an explicit "+" on gains, the locale's own "-" on
 * losses. `Intl` gives us the minus but never the plus, and a P&L that reads "12,480" when
 * it is a gain and "-12,480" when it is a loss makes the two look like different kinds of
 * number at a glance. Null renders as an em dash, never as zero -- "no data" and "flat" are
 * not the same statement on a trading screen.
 */
export const formatSignedCurrency = (value: Numeric, currency = 'INR', decimals = 0): string => {
  const n = toNum(value);
  if (n == null) return '—';
  return (n >= 0 ? '+' : '') + formatCurrency(n, currency, decimals);
};

/**
 * Indian-format compaction for a tight space: 8,42,900 -> ₹8.43L, 1,05,00,000 -> ₹1.05Cr.
 *
 * `abbreviateNumber` above cannot do this job: it is K/M/B, and its `n >= 1e3` ladder means
 * every NEGATIVE value falls through and returns unformatted (`abbreviateNumber(-500000)`
 * is the string "-500000"). Half of what this app renders is a loss.
 */
export const formatCompactInr = (value: Numeric, signed = false): string => {
  const n = toNum(value);
  if (n == null) return '—';
  const sign = n < 0 ? '-' : (signed ? '+' : '');
  const a = Math.abs(n);
  if (a >= 1e7)  return `${sign}₹${(a / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5)  return `${sign}₹${(a / 1e5).toFixed(2)}L`;
  if (a >= 1e3)  return `${sign}₹${(a / 1e3).toFixed(1)}K`;
  return `${sign}₹${a.toFixed(0)}`;
};

export const formatDate = (date: Date | string): string => {
  return new Date(date).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatTime = (date: Date | string): string => {
  return new Date(date).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

export const formatDateTime = (date: Date | string): string => {
  return `${formatDate(date)} ${formatTime(date)}`;
};

/* Same omission as formatPercentage: `value >= 1e9` on a numeric string does
   coerce, but `.toFixed()` on the division result was only safe by accident. */
export const abbreviateNumber = (value: Numeric): string => {
  const n = toNum(value);
  if (n == null) return String(value ?? '');
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return String(n);
};
